import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Download, Eye, MousePointerClick, Package2, Plus, Target } from "lucide-react";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "../contexts/AuthContext";
import { activateAd, approveAd, deleteAd, getAdsOverview, listAds, pauseAd, rejectAd } from "../api/ads";

const statusVariant = {
    active: "default",
    paused: "secondary",
    rejected: "destructive",
};

const moderationVariant = {
    approved: "default",
    pending: "outline",
    rejected: "destructive",
    paused: "secondary",
};

function metricNumber(value, suffix = "") {
    return `${new Intl.NumberFormat("sv-SE").format(Number(value || 0))}${suffix}`;
}

function targetLabel(ad) {
    if (ad.target_mode === "all") return "Nationell";
    if (Array.isArray(ad.club_slugs) && ad.club_slugs.length) return ad.club_slugs.join(", ");
    return "-";
}

function placementLabel(ad) {
    if (Array.isArray(ad.placements) && ad.placements.length) return ad.placements.join(", ");
    return ad.placement || "banner";
}

function downloadCsv(filename, rows) {
    const csv = rows
        .map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(";"))
        .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

const slotCatalog = [
    {
        placement: "banner_protocol",
        status: "Aktiv",
        slots: "protocol-top, protocol-bottom",
        description: "Två banners i matchprotokoll. En ovanför heatprogrammet och en längre ned i protokollet.",
    },
    {
        placement: "banner",
        status: "Fallback",
        slots: "Generell backup",
        description: "Visas när en specifik placement saknar annons och systemet behöver en generell banner.",
    },
    {
        placement: "banner_home",
        status: "Aktiv",
        slots: "home-hero",
        description: "Generell banner på startsidan under hero-sektionen. Styrs inte av lagkontext.",
    },
    {
        placement: "banner_live",
        status: "Förberedd",
        slots: "-",
        description: "Ingen aktiv frontendyta ännu. Backendstöd och adminval finns kvar för framtida live-del.",
    },
];

function StatCard({ icon: Icon, title, value, hint }) {
    return (
        <Card className="border-border/80 shadow-sm">
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-sm text-muted-foreground">{title}</div>
                        <div className="mt-2 text-3xl font-semibold text-foreground">{value}</div>
                        {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
                    </div>
                    <div className="rounded-full border border-border/80 bg-muted/40 p-2.5 text-muted-foreground">
                        <Icon className="h-4 w-4" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function AdsListPage() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [placementFilter, setPlacementFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [moderationFilter, setModerationFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const canCreate = user?.role === "club_admin" || user?.role === "super_admin";
    const canModerate = user?.role === "super_admin";

    const load = async () => {
        try {
            setLoading(true);
            const [ads, overviewData] = await Promise.all([
                listAds(),
                getAdsOverview({
                    date_from: dateFrom || undefined,
                    date_to: dateTo || undefined,
                }),
            ]);
            setItems(Array.isArray(ads) ? ads : []);
            setOverview(overviewData || null);
        } catch (err) {
            toast.error("Kunde inte hämta annonser");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [dateFrom, dateTo]);

    const placements = useMemo(() => Object.entries(overview?.placements || {}), [overview]);
    const customerRows = useMemo(() => overview?.customers || [], [overview]);
    const filteredItems = useMemo(() => {
        const term = search.trim().toLowerCase();
        return items.filter((ad) => {
            const matchesSearch = !term || [
                ad.customer_name,
                ad.title,
                ad.club_slug,
                ...(ad.club_slugs || []),
                ...(ad.placements || []),
                ad.placement,
            ].some((value) => (value || "").toString().toLowerCase().includes(term));
            const matchesPlacement = placementFilter === "all" || (ad.placements || [ad.placement]).includes(placementFilter);
            const matchesStatus = statusFilter === "all" || ad.status === statusFilter;
            const matchesModeration = moderationFilter === "all" || ad.moderation_status === moderationFilter;
            return matchesSearch && matchesPlacement && matchesStatus && matchesModeration;
        });
    }, [items, moderationFilter, placementFilter, search, statusFilter]);

    const reportCsvRows = useMemo(() => {
        return [
            ["Kund", "Antal annonser", "Visningar", "Klick", "CTR"],
            ...customerRows.map((customer) => [
                customer.customer_name,
                customer.ads,
                customer.impressions,
                customer.clicks,
                `${customer.ctr}%`,
            ]),
        ];
    }, [customerRows]);

    async function act(id, fn, successText) {
        try {
            await fn(id);
            await load();
            toast.success(successText);
        } catch (err) {
            toast.error(err?.message || "Åtgärden misslyckades");
        }
    }

    return (
        <div className="min-h-screen bg-background">
            <AdminHeader />
            <main className="container mx-auto px-4 py-6 space-y-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div className="space-y-1">
                        <h1 className="text-2xl font-semibold text-foreground">Annonser</h1>
                        <p className="text-sm text-muted-foreground">
                            Hantera annonsytor, moderering, klick och synlighet för företag som köper plats i appen.
                        </p>
                    </div>
                    {canCreate ? (
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full sm:w-auto" />
                            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full sm:w-auto" />
                            <Button variant="outline" onClick={() => downloadCsv("annonsrapport-kunder.csv", reportCsvRows)}>
                                <Download className="mr-2 h-4 w-4" />
                                Exportera CSV
                            </Button>
                            <Button asChild className="w-full md:w-auto">
                                <Link to="/admin/ads/new"><Plus className="mr-2 h-4 w-4" />Ny annons</Link>
                            </Button>
                        </div>
                    ) : null}
                </div>

                {loading ? (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-32 w-full" />)}
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <StatCard icon={Package2} title="Antal annonser" value={metricNumber(overview?.total_ads)} hint={`${metricNumber(overview?.active_ads)} aktiva`} />
                        <StatCard icon={Eye} title="Visningar" value={metricNumber(overview?.total_impressions)} hint={`${metricNumber(overview?.pending_ads)} väntar på moderering`} />
                        <StatCard icon={MousePointerClick} title="Klick" value={metricNumber(overview?.total_clicks)} hint="Serverloggade klick" />
                        <StatCard icon={Target} title="CTR" value={metricNumber(overview?.ctr, "%")} hint="Klick per visning" />
                    </div>
                )}

                <div className="grid gap-4 xl:grid-cols-[1.3fr_0.7fr]">
                    <Card className="min-w-0 border-border/80 shadow-sm">
                        <CardHeader>
                            <CardTitle>Annonslista</CardTitle>
                            <CardDescription>Överblick över aktiva, pausade och väntande annonser.</CardDescription>
                        </CardHeader>
                        <CardContent className="min-w-0">
                            <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Sök kund, titel eller klubb"
                                />
                                <Select value={placementFilter} onValueChange={setPlacementFilter}>
                                    <SelectTrigger className="bg-[hsl(var(--card))] text-foreground border-[hsl(var(--border))]">
                                        <SelectValue placeholder="Alla placeringar" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-background text-popover-foreground border shadow-md">
                                        <SelectItem value="all">Alla placeringar</SelectItem>
                                        <SelectItem value="banner">Generell banner</SelectItem>
                                        <SelectItem value="banner_home">Hem</SelectItem>
                                        <SelectItem value="banner_protocol">Protokoll</SelectItem>
                                        <SelectItem value="banner_live">Live</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="bg-[hsl(var(--card))] text-foreground border-[hsl(var(--border))]">
                                        <SelectValue placeholder="Alla statusar" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-background text-popover-foreground border shadow-md">
                                        <SelectItem value="all">Alla statusar</SelectItem>
                                        <SelectItem value="active">Aktiv</SelectItem>
                                        <SelectItem value="paused">Pausad</SelectItem>
                                        <SelectItem value="rejected">Avvisad</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={moderationFilter} onValueChange={setModerationFilter}>
                                    <SelectTrigger className="bg-[hsl(var(--card))] text-foreground border-[hsl(var(--border))]">
                                        <SelectValue placeholder="All moderering" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-background text-popover-foreground border shadow-md">
                                        <SelectItem value="all">All moderering</SelectItem>
                                        <SelectItem value="approved">Godkänd</SelectItem>
                                        <SelectItem value="pending">Väntar</SelectItem>
                                        <SelectItem value="rejected">Avvisad</SelectItem>
                                        <SelectItem value="paused">Pausad</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {loading ? (
                                <div className="space-y-3">
                                    {Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}
                                </div>
                            ) : filteredItems.length === 0 ? (
                                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                                    Inga annonser matchar filtreringen.
                                </div>
                            ) : (
                                <div className="w-full overflow-x-auto">
                                <Table className="min-w-[1180px]">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Kund</TableHead>
                                            <TableHead>Annons</TableHead>
                                            <TableHead>Målgrupp</TableHead>
                                            <TableHead>Placering</TableHead>
                                            <TableHead>Period</TableHead>
                                            <TableHead>Visningar</TableHead>
                                            <TableHead>Klick</TableHead>
                                            <TableHead>CTR</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Åtgärder</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredItems.map((ad) => (
                                            <TableRow key={ad.id}>
                                                <TableCell className="font-medium">{ad.customer_name || "-"}</TableCell>
                                                <TableCell>
                                                    <div className="space-y-1">
                                                        <div className="font-medium text-foreground">{ad.title}</div>
                                                        <div className="text-xs text-muted-foreground">{ad.package} • vikt {ad.weight}</div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{targetLabel(ad)}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{placementLabel(ad)}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{ad.start_date} - {ad.end_date}</TableCell>
                                                <TableCell>{metricNumber(ad.impressions)}</TableCell>
                                                <TableCell>{metricNumber(ad.clicks)}</TableCell>
                                                <TableCell>{metricNumber(ad.ctr, "%")}</TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col gap-2">
                                                        <Badge variant={statusVariant[ad.status] || "secondary"} className="capitalize w-fit">{ad.status}</Badge>
                                                        <Badge variant={moderationVariant[ad.moderation_status] || "outline"} className="capitalize w-fit">{ad.moderation_status}</Badge>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex flex-wrap justify-end gap-2">
                                                        <Button asChild size="sm" variant="outline">
                                                            <Link to={`/admin/ads/${ad.id}/edit`}>Redigera</Link>
                                                        </Button>
                                                        {canModerate ? (
                                                            <>
                                                                {ad.moderation_status !== "approved" ? (
                                                                    <Button size="sm" variant="secondary" onClick={() => act(ad.id, approveAd, "Annonsen godkändes")}>Godkänn</Button>
                                                                ) : null}
                                                                {ad.moderation_status !== "rejected" ? (
                                                                    <Button size="sm" variant="outline" onClick={() => act(ad.id, rejectAd, "Annonsen avvisades")}>Avvisa</Button>
                                                                ) : null}
                                                                {ad.status === "active" ? (
                                                                    <Button size="sm" variant="outline" onClick={() => act(ad.id, pauseAd, "Annonsen pausades")}>Pausa</Button>
                                                                ) : (
                                                                    <Button size="sm" variant="outline" onClick={() => act(ad.id, activateAd, "Annonsen aktiverades")}>Aktivera</Button>
                                                                )}
                                                                <Button size="sm" variant="destructive" onClick={() => act(ad.id, deleteAd, "Annonsen raderades")}>Radera</Button>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="min-w-0 space-y-4">
                        <Card className="border-border/80 shadow-sm">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <BarChart3 className="h-4 w-4" />
                                    Rapport per kund
                                </CardTitle>
                                <CardDescription>Klick, visningar och CTR grupperat per kund.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {loading ? (
                                    Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-14 w-full" />)
                                ) : customerRows.length ? (
                                    customerRows.map((customer) => (
                                        <Link
                                            key={customer.customer_name}
                                            to={`/admin/ads/customers/${encodeURIComponent(customer.customer_name)}${dateFrom || dateTo ? `?${new URLSearchParams({ ...(dateFrom ? { date_from: dateFrom } : {}), ...(dateTo ? { date_to: dateTo } : {}) }).toString()}` : ""}`}
                                            className="block rounded-lg border p-3 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="font-medium text-foreground underline-offset-4">{customer.customer_name}</div>
                                                <Badge variant="outline">{metricNumber(customer.ads)} annonser</Badge>
                                            </div>
                                            <div className="mt-2 text-sm text-muted-foreground">
                                                {metricNumber(customer.impressions)} visningar • {metricNumber(customer.clicks)} klick • CTR {metricNumber(customer.ctr, "%")}
                                            </div>
                                            <div className="mt-2 text-xs text-primary">Öppna kundrapport</div>
                                        </Link>
                                    ))
                                ) : (
                                    <div className="text-sm text-muted-foreground">Ingen statistik ännu.</div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="border-border/80 shadow-sm">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <BarChart3 className="h-4 w-4" />
                                    Placeringar
                                </CardTitle>
                                <CardDescription>Hur annonsstocken fördelar sig mellan olika ytor.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {loading ? (
                                    Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-14 w-full" />)
                                ) : placements.length ? (
                                    placements.map(([placement, stats]) => (
                                        <div key={placement} className="rounded-lg border p-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="font-medium text-foreground">{placement}</div>
                                                <Badge variant="outline">{metricNumber(stats.ads)} annonser</Badge>
                                            </div>
                                            <div className="mt-2 text-sm text-muted-foreground">
                                                {metricNumber(stats.impressions)} visningar • {metricNumber(stats.clicks)} klick
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-sm text-muted-foreground">Ingen statistik ännu.</div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="border-border/80 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-base">Aktiva annonsytor i appen</CardTitle>
                                <CardDescription>Visar vilka placements och slotar som faktiskt används just nu.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {slotCatalog.map((slot) => (
                                    <div key={slot.placement} className="rounded-lg border p-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="font-medium text-foreground">{slot.placement}</div>
                                            <Badge variant={slot.status === "Aktiv" ? "default" : "outline"}>{slot.status}</Badge>
                                        </div>
                                        <div className="mt-2 text-sm text-muted-foreground">{slot.description}</div>
                                        <div className="mt-1 text-xs text-muted-foreground">Slotar: {slot.slots}</div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        <Card className="border-border/80 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-base">Så fungerar annonseringen</CardTitle>
                                <CardDescription>Kort om nuvarande spårning och flöde.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm text-muted-foreground">
                                <p>Klick loggas server-side innan användaren skickas vidare till annonsörens mållänk.</p>
                                <p>Visningar loggas när en banner väljs och renderas i appen.</p>
                                <p>Moderering krävs innan en annons kan visas publikt.</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
}
